// Waiting for a server, with a limit on the waiting and not on the answer.
//
// THE FAILURE THIS EXISTS FOR. A connection that is REFUSED fails immediately and the app has
// always handled it — boot catches, and bootFailed offers "You are offline" and a Try again. A
// connection that is ACCEPTED and then goes quiet is the other case: a wedged sidecar, a paused
// container, a proxy holding the socket. fetch() never settles, so boot sits on its first await
// with "Loading…" on screen — no error, no button, nothing but a manual reload. Kennard was left
// there when a local server went away underneath a tab (MYN-152).
//
// HERE RATHER THAN IN app.ts so it can be tested against a fetch that never answers, which is the
// only way to check the thing that matters. web/app.ts is a bundle with a DOM under it; this is a
// function. The same move shared/lookup.ts made, for the same reason.

/** The server took the request and said nothing. Not the same as being unable to reach it. */
export class NoAnswer extends Error {
  constructor(what: string) {
    super(`the server did not answer ${what}`);
    this.name = 'NoAnswer';
  }
}

/** Just enough of fetch to stand in for it. */
export type Fetching = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Fetch, giving up if no answer ARRIVES within `ms`.
 *
 * NOT A LIMIT ON THE DOWNLOAD, and the difference is the whole point. Boot asks for about 6 MB of
 * corpus; on a slow link that is minutes, and a wall-clock timeout would abort a connection that
 * was working perfectly — on exactly the connections this app has to survive. fetch() settles as
 * soon as the response HEADERS arrive, so the clock stops there and the body streams for as long
 * as it needs.
 *
 * WHAT IT DOES NOT COVER, said out loud: a body that starts and then stalls mid-stream. That needs
 * a different instrument — a read that watches for silence rather than a timer on the whole — and
 * it is much the rarer failure. A server that never answers at all is the common one.
 */
export async function fetchAnswering(
  fetching: Fetching,
  input: string,
  init: RequestInit | undefined,
  ms: number,
): Promise<Response> {
  const giveUp = new AbortController();
  const timer = setTimeout(() => giveUp.abort(), ms);
  try {
    return await fetching(input, { ...init, signal: giveUp.signal });
  } catch (err) {
    // An abort here is ours: nothing else aborts these.
    if (giveUp.signal.aborted) throw new NoAnswer(input);
    throw err;
  } finally {
    // Cleared the moment the headers land, so reading the body is not on the clock.
    clearTimeout(timer);
  }
}
